import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Shield, ShieldOff } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import {
  AdminUser, useAdminUsers, useSetUserRole, useSetUserStaff, UserRole,
} from '@/lib/admin';
import { useDebounce } from '@/hooks';
import { confirm } from '@/components/ui/ConfirmDialog';

const ROLES: UserRole[] = ['owner', 'admin', 'manager', 'member', 'employee'];

export function AdminUsersPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const users = useAdminUsers(debouncedSearch);

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto space-y-6">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('admin.eyebrow')}</p>
          <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('admin.users.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('admin.users.subtitle')}</p>
        </header>

        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.users.searchPlaceholder')}
            className="w-full h-11 rounded-xl border border-border bg-card ps-10 pe-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>

        <div className="rounded-2xl border border-border bg-card shadow-soft overflow-hidden">
          {users.isLoading ? (
            <p className="px-5 py-8 text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : users.isError ? (
            <div className="px-5 py-12 text-center">
              <p className="text-sm font-medium text-rose-600">
                {t('admin.users.loadError', { defaultValue: "Couldn't load users. Try again." })}
              </p>
              <button
                onClick={() => users.refetch()}
                className="mt-3 inline-flex h-9 items-center px-4 rounded-lg bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20"
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </button>
            </div>
          ) : (users.data ?? []).length === 0 ? (
            <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t('admin.users.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-start">
                  <tr>
                    <th className="px-4 py-3 font-semibold">{t('admin.users.col.user')}</th>
                    <th className="px-4 py-3 font-semibold">{t('admin.users.col.company')}</th>
                    <th className="px-4 py-3 font-semibold">{t('admin.users.col.role')}</th>
                    <th className="px-4 py-3 font-semibold">{t('admin.users.col.staff')}</th>
                    <th className="px-4 py-3 font-semibold">{t('admin.users.col.created')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(users.data ?? []).map((u, i) => (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(i * 0.02, 0.3) }}
                      className="hover:bg-muted/20 transition"
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold">{u.displayName}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                        {!u.emailVerified && (
                          <span className="inline-block mt-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 font-semibold">
                            {t('admin.users.unverified')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {u.companyName ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <RoleCell user={u} />
                      </td>
                      <td className="px-4 py-3">
                        <StaffToggle user={u} />
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground num">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function RoleCell({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const setRole = useSetUserRole();
  return (
    <select
      value={user.role}
      onChange={async (e) => {
        const role = e.target.value as UserRole;
        if (role === user.role) return;
        const ok = await confirm({
          title: t('admin.users.confirmRoleTitle'),
          description: t('admin.users.confirmRoleBody', { email: user.email, role: t(`admin.users.role.${role}`) }),
          confirmLabel: t('common.confirm'),
        });
        if (!ok) return;
        setRole.mutate({ id: user.id, role });
      }}
      className="h-9 rounded-lg border border-border bg-card px-2 text-xs"
    >
      {ROLES.map((r) => (
        <option key={r} value={r}>{t(`admin.users.role.${r}`)}</option>
      ))}
    </select>
  );
}

function StaffToggle({ user }: { user: AdminUser }) {
  const { t } = useTranslation();
  const setStaff = useSetUserStaff();
  return (
    <button
      onClick={async () => {
        const ok = await confirm({
          title: user.isStaff ? t('admin.users.confirmRevokeStaffTitle') : t('admin.users.confirmGrantStaffTitle'),
          description: user.isStaff
            ? t('admin.users.confirmRevokeStaffBody', { email: user.email })
            : t('admin.users.confirmGrantStaffBody',  { email: user.email }),
          tone: user.isStaff ? 'destructive' : 'default',
          confirmLabel: user.isStaff ? t('admin.users.revoke') : t('admin.users.grant'),
        });
        if (!ok) return;
        setStaff.mutate({ id: user.id, isStaff: !user.isStaff });
      }}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
        user.isStaff
          ? 'bg-primary/15 text-primary'
          : 'bg-muted text-muted-foreground hover:text-foreground'
      }`}
    >
      {user.isStaff ? <Shield className="h-3.5 w-3.5" /> : <ShieldOff className="h-3.5 w-3.5" />}
      {user.isStaff ? t('admin.users.staff') : t('admin.users.notStaff')}
    </button>
  );
}
